// Promise wrapper around the engine worker.
import type { Envelope, Request, Response } from './protocol.ts';

type Of<T extends Response['type']> = Extract<Response, { type: T }>;

export class EngineClient {
  private worker: Worker;
  private seq = 0;
  private pending = new Map<number, { resolve: (r: Response) => void; reject: (e: Error) => void }>();
  /** told of every request when it settles (the debug log) */
  onSettled?: (req: Request, ms: number, res?: Response, err?: Error & { engineStack?: string; shapes?: string[][]; forceable?: boolean }) => void;

  /** a reason to refuse the request before it reaches the engine (View mode, D-74), or undefined to send it */
  refuse?: (req: Request) => string | undefined;

  constructor() {
    this.worker = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent<Envelope<Response>>) => {
      const p = this.pending.get(e.data.id);
      if (!p) return;
      this.pending.delete(e.data.id);
      if (e.data.error) p.reject(Object.assign(new Error(e.data.error), { engineStack: e.data.stack, shapes: e.data.shapes, forceable: e.data.forceable }));
      else p.resolve(e.data.body);
    };
  }

  call<T extends Request>(req: T): Promise<Of<T['type']>> {
    const refused = this.refuse?.(req);
    if (refused) return Promise.reject(new Error(refused));
    const id = ++this.seq;
    const transfer = 'bytes' in req ? [req.bytes] : [];
    const t0 = performance.now();
    const settled = this.onSettled;
    const tell = (r?: Response, e?: Error) => {
      try {
        settled?.(sent, performance.now() - t0, r, e);
      } catch {
        // The log must never break the request.
      }
    };
    // Read before the bytes are transferred away.
    const sent = settled && 'bytes' in req ? { ...req, bytes: { byteLength: req.bytes.byteLength } as ArrayBuffer } : req;
    return new Promise((resolve, reject) => {
      this.pending.set(id, {
        resolve: (r) => {
          tell(r);
          (resolve as (r: Response) => void)(r);
        },
        reject: (e) => {
          tell(undefined, e);
          reject(e);
        },
      });
      this.worker.postMessage({ id, body: req } satisfies Envelope<Request>, transfer);
    });
  }
}
